// ============================================================================
// RECOVERABLE EXPENSE capture (ADR-0037 / ADR-0038).
//
// Records an expense a BU incurs on behalf of a customer. On save it posts
// Dr Reimbursable-Expenses (recoverable, open-item-managed) / Cr Cash-Bank,
// creating a DR (AR-natured) open item = pending reimbursement, later recharged
// on a sales invoice.
//
// The recovering BU/entity may differ from the paying one (ADR-0038): same
// entity -> BU dimensions + the engine's balanced-BU expansion; different
// entity -> an automatic intercompany pair.
//
// The chosen reimbursement item supplies the recoverable ledger; the backend
// GUARDS that it is open-item-managed + asset-natured.
// ============================================================================

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect,
  ColumnSelector, type ColumnDefinition, useColumnVisibility,
} from 'components/ui';
import { EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan } from 'components/enterprise-shell';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { recoverableExpenseService, partyService, serviceService, coaLedgerService } from 'api/services';
import type {
  RecoverableExpenseDto, RecoverableExpenseQueryParams, CreateRecoverableExpenseRequest,
  RecoveryTargetDto,
} from 'api/types';
import { useAuthContext } from 'contexts/AuthContext';
import { FyDateInput } from 'components/common/FyDateInput';
import { useSelectedFinancialYear, defaultDateIn } from 'hooks/useSelectedFinancialYear';

/** Encoded "recover through" picker value: `${entityId}::${businessUnitId}` (mirrors receipts). */
const TARGET_SEP = '::';

type ViewMode = 'list' | 'grid';

const today = () => new Date().toISOString().slice(0, 10);
const fmtMoney = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_LABEL: Record<string, string> = { OPEN: 'Pending', PARTIAL: 'Partly reimbursed', CLOSED: 'Reimbursed' };
const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'OPEN', label: 'Pending / partly reimbursed' },
  { value: 'SETTLED', label: 'Fully reimbursed' },
];

interface FormData {
  expenseDate: string;
  partyId: string;
  serviceId: string;
  recoverableLedgerLabel: string;
  recoverTarget: string;
  paidFromLedgerId: string;
  amount: string;
  description: string;
}
const EMPTY: FormData = {
  expenseDate: today(), partyId: '', serviceId: '',
  recoverableLedgerLabel: '', recoverTarget: '', paidFromLedgerId: '', amount: '', description: '',
};

// ---------------------------------------------------------------------------
// FORM MODAL (create-only — a capture posts immediately)
// ---------------------------------------------------------------------------

interface ExpenseFormModalProps {
  isOpen: boolean;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName: string;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
  onRestoreModal?: (instanceId?: string) => void;
}

const ExpenseFormModal: React.FC<ExpenseFormModalProps> = ({
  isOpen, onSaved, onClose, tabId, moduleName, createInstanceId, minimizeRef, onRestoreModal,
}) => {
  const fy = useSelectedFinancialYear();
  const fyRef = useRef(fy); fyRef.current = fy;
  /** Default posting date: today when it falls in the selected financial year, else the year's last (or first) day. */
  const docToday = () => defaultDateIn(fyRef.current);
  const { selectedEntity, selectedBusinessUnit } = useAuthContext();
  const { minimize } = useModalMinimize();
  const [form, setForm] = useState<FormData>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [targets, setTargets] = useState<RecoveryTargetDto[]>([]);
  const [discardOpen, setDiscardOpen] = useState(false);

  const formRef = useRef(form); formRef.current = form;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  const ownTarget = selectedEntity && selectedBusinessUnit
    ? `${selectedEntity.id}${TARGET_SEP}${selectedBusinessUnit.id}` : '';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    recoverableExpenseService.getRecoveryTargets().then(setTargets).catch(() => setTargets([]));
    if (isRestoreRef.current) { isRestoreRef.current = false; setErrors({}); return; }
    const data = { ...EMPTY, expenseDate: docToday(), recoverTarget: ownTarget };
    setForm(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(form) !== initialSnapshotRef.current;
  }, [form]);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardOpen(true); else onClose();
  }, [isDirty, onClose]);

  // --- Minimize: park this capture and resume it later from the toolbar strip.
  const handleMinimize = useCallback(() => {
    if (!tabId) return;
    const idx = createInstanceIdRef.current || '1';
    const savedForm = { ...formRef.current };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedForm) !== savedSnapshot : false;
    const title = savedForm.amount ? `Add - ${savedForm.amount} (${idx})` : `Add New (${idx})`;

    minimize({
      id: `${tabId}-form-modal-create-${idx}`,
      title, moduleName, tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(savedForm);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(idx);
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const err = (f: string) => errors[f];
  const clear = (f: string) => setErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; });

  const loadParties = useCallback(async (input: string) => {
    const res = await partyService.getAll({ pageSize: 50, searchTerm: input || undefined, partyType: 'CUSTOMER' });
    return res.data.map(p => ({ value: p.id, label: `${p.partyCode} — ${p.partyName}` }));
  }, []);

  const loadReimbItems = useCallback(async (input: string) => {
    const res = await serviceService.getAll({ pageSize: 50, searchTerm: input || undefined, itemType: 'REIMBURSEMENT' });
    return res.data.map(s => ({ value: s.id, label: `${s.serviceCode} — ${s.serviceName}` }));
  }, []);

  const loadCashBank = useCallback(async (input: string) => {
    // ledgerKind GL first — otherwise the page fills with party subledgers and the
    // subtype filter below has nothing left to match (ADR-0040).
    const res = await coaLedgerService.getAll({ pageSize: 100, searchTerm: input || undefined, ledgerKind: 'GL' });
    return res.data
      .filter(l => l.subtypeCode === 'BANK_ACCOUNT' || l.subtypeCode === 'CASH_ACCOUNT')
      .map(l => ({ value: l.id, label: `${l.ledgerCode} — ${l.ledgerName}` }));
  }, []);

  const ownCurrency = useMemo(
    () => targets.find(t => t.entityId === selectedEntity?.id)?.functionalCurrencyCode || '',
    [targets, selectedEntity],
  );
  const targetOptions = useMemo(() => {
    const opts: { value: string; label: string; isDisabled?: boolean }[] = [];
    const ordered = [...targets].sort((a, b) => {
      if (a.entityId === selectedEntity?.id) return -1;
      if (b.entityId === selectedEntity?.id) return 1;
      return (a.entityName || '').localeCompare(b.entityName || '');
    });
    for (const t of ordered) {
      const isOwn = t.entityId === selectedEntity?.id;
      const mismatch = !isOwn && !!ownCurrency && !!t.functionalCurrencyCode
        && t.functionalCurrencyCode.toUpperCase() !== ownCurrency.toUpperCase();
      for (const bu of t.businessUnits || []) {
        opts.push({
          value: `${t.entityId}${TARGET_SEP}${bu.businessUnitId}`,
          label: isOwn ? (bu.unitName || '') : `${t.entityName} — ${bu.unitName}${mismatch ? ' (different functional currency — not routable in this version)' : ''}`,
          isDisabled: mismatch,
        });
      }
    }
    return opts;
  }, [targets, selectedEntity, ownCurrency]);

  const isCrossEntity = !!form.recoverTarget
    && form.recoverTarget.split(TARGET_SEP)[0] !== (selectedEntity?.id || '');

  const handleSave = useCallback(async () => {
    const e: Record<string, string> = {};
    if (!form.partyId) e.partyId = 'Customer is required';
    if (!form.serviceId) e.serviceId = 'Reimbursement item is required';
    if (!form.recoverTarget) e.recoverTarget = 'Select who will recover this from the customer';
    if (!form.paidFromLedgerId) e.paidFromLedgerId = 'Paid-from ledger is required';
    const amt = Number(form.amount);
    if (!form.amount || Number.isNaN(amt) || amt <= 0) e.amount = 'Enter an amount greater than zero';
    if (!form.expenseDate) e.expenseDate = 'Date is required';
    if (Object.keys(e).length) { setErrors(e); return; }

    setSaving(true);
    try {
      const [recEntityId, recBuId] = form.recoverTarget.split(TARGET_SEP);
      const payload: CreateRecoverableExpenseRequest = {
        expenseDate: form.expenseDate,
        partyId: form.partyId,
        serviceId: form.serviceId,
        paidFromLedgerId: form.paidFromLedgerId,
        recoveryEntityId: recEntityId || null,
        recoveryBusinessUnitId: recBuId || null,
        amount: amt,
        description: form.description.trim() || null,
      };
      const saved = await recoverableExpenseService.create(payload);
      setSaving(false);
      notificationService.toast.success(`Recoverable expense ${saved.expenseNumber || ''} captured.`);
      onSaved(); onClose();
    } catch (err: any) {
      setSaving(false);
      // The backend surfaces the ledger-setup guard message here.
      notificationService.toast.error(err?.response?.data?.message || 'Could not capture the expense.', 7000);
    }
  }, [form, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {saving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Posting...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Capture Recoverable Expense</h2>
          <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            <div className="form-group" data-field="expenseDate">
              <label className="form-label">Expense Date <span className="text-danger">*</span></label>
              <FyDateInput className={`form-input${err('expenseDate') ? ' form-field-error' : ''}`}
                value={form.expenseDate} onChange={v => { clear('expenseDate'); setForm(p => ({ ...p, expenseDate: v })); }} />
              {err('expenseDate') && <div className="form-error-message">{err('expenseDate')}</div>}
            </div>
            <div className="form-group" data-field="amount">
              <label className="form-label">Amount <span className="text-danger">*</span></label>
              <input type="number" min={0} step="0.01" className={`form-input${err('amount') ? ' form-field-error' : ''}`}
                value={form.amount} onChange={e => { clear('amount'); setForm(p => ({ ...p, amount: e.target.value })); }} />
              {err('amount') && <div className="form-error-message">{err('amount')}</div>}
            </div>
          </div>

          <div className="form-group" data-field="partyId">
            <SyutSelect label={<>Customer <span className="text-danger">*</span></>} isAsync cacheOptions={false} defaultOptions
              loadOptions={loadParties} value={form.partyId}
              onChange={v => { clear('partyId'); setForm(p => ({ ...p, partyId: (v as string) || '' })); }}
              error={err('partyId')} menuPortalTarget={document.body} />
          </div>

          <div className="form-group" data-field="serviceId">
            <SyutSelect label={<>Reimbursement Item <span className="text-danger">*</span></>} isAsync cacheOptions={false} defaultOptions
              loadOptions={loadReimbItems} value={form.serviceId}
              onChange={async v => {
                const id = (v as string) || '';
                clear('serviceId');
                setForm(p => ({ ...p, serviceId: id, recoverableLedgerLabel: '' }));
                if (id) {
                  try {
                    const svc = await serviceService.getById(id);
                    setForm(p => (p.serviceId === id ? { ...p, recoverableLedgerLabel: svc.incomeLedgerName || svc.incomeLedgerCode || '' } : p));
                  } catch { /* best-effort hint only */ }
                }
              }}
              error={err('serviceId')} menuPortalTarget={document.body} />
            {form.recoverableLedgerLabel && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                Recoverable ledger: <strong>{form.recoverableLedgerLabel}</strong> (must be an Asset, open-item-managed control)
              </div>
            )}
          </div>

          <div className="form-group" data-field="recoverTarget">
            <SyutSelect label={<>Recover Through <span className="text-danger">*</span></>}
              options={targetOptions} value={form.recoverTarget}
              onChange={v => { clear('recoverTarget'); setForm(p => ({ ...p, recoverTarget: (v as string) || '' })); }}
              isClearable={false} error={err('recoverTarget')} menuPortalTarget={document.body} />
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
              The business unit that will invoice the customer. Defaults to yours — change it when
              you are paying on behalf of another branch or company.
            </div>
            {isCrossEntity && (
              <div style={{ fontSize: '0.72rem', color: 'var(--warning-text, #92400e)', background: 'var(--warning-bg, #fef3c7)', border: '1px solid #fde68a', borderRadius: '0.4rem', padding: '0.35rem 0.5rem', marginTop: '0.35rem' }}>
                Cross-company: an intercompany pair will be posted automatically — the paying company
                records an amount due from the recovering company, which carries the recoverable.
              </div>
            )}
          </div>

          <div className="form-group" data-field="paidFromLedgerId">
            <SyutSelect label={<>Paid From (Cash / Bank) <span className="text-danger">*</span></>} isAsync cacheOptions={false} defaultOptions
              loadOptions={loadCashBank} value={form.paidFromLedgerId}
              onChange={v => { clear('paidFromLedgerId'); setForm(p => ({ ...p, paidFromLedgerId: (v as string) || '' })); }}
              error={err('paidFromLedgerId')} placeholder="Cash / bank ledger" menuPortalTarget={document.body} />
          </div>

          <div className="form-group" data-field="description">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows={2} value={form.description}
              placeholder="What the expense was for (e.g. CIPA annual return filing fee)"
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>Post Expense</button>
        </div>
      </div>

      <ConfirmDialog
        open={discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardOpen(false); onClose(); }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// SCREEN
// ---------------------------------------------------------------------------

const STRUCTURAL_COLUMN_IDS = ['select', 'actions'];

const COLUMN_DEFS: ColumnDefinition[] = [
  { id: 'select', label: '', required: true },
  { id: 'expenseNumber', label: 'Number' },
  { id: 'expenseDate', label: 'Date' },
  { id: 'partyName', label: 'Customer' },
  { id: 'serviceName', label: 'Item' },
  { id: 'recovery', label: 'Recovered By' },
  { id: 'amount', label: 'Amount' },
  { id: 'pendingAmount', label: 'Pending' },
  { id: 'reimbursementStatus', label: 'Status' },
  { id: 'actions', label: 'Actions', required: true },
];

interface ScreenProps { moduleIcon?: string; tabId?: string; moduleName?: string; }

const RecoverableExpenseScreen: React.FC<ScreenProps> = ({ moduleIcon, tabId, moduleName }) => {
  const can = useCan('recoverable-expense');
  const { confirmDelete, ConfirmDialogPortal } = useStandardConfirms();
  const { isMinimized } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<RecoverableExpenseQueryParams>({ page: 1, pageSize: 25 });
  const [data, setData] = useState<RecoverableExpenseDto[]>([]);
  const [pagination, setPagination] = useState({ totalItems: 0 });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const { visibleColumns, onApply: applyColumns, onReset: resetColumns } =
    useColumnVisibility('recoverable-expense-columns', COLUMN_DEFS);

  // --- filter panel (draft -> Apply, consistent with the reports) ---
  const [filterOpen, setFilterOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [createInstanceId, setCreateInstanceId] = useState('1');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await recoverableExpenseService.getAll(queryParams);
      setData(res.data); setPagination({ totalItems: res.pagination.totalItems });
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not load expenses.');
      setData([]);
    } finally { setLoading(false); }
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handleAdd = useCallback(() => {
    let next = 1;
    if (tabId) { while (isMinimized(`${tabId}-form-modal-create-${next}`)) next++; }
    setCreateInstanceId(String(next));
    setFormOpen(true);
  }, [tabId, isMinimized]);

  const handleRestoreModal = useCallback((instanceId?: string) => {
    if (instanceId) setCreateInstanceId(instanceId);
    setFormOpen(true);
  }, []);

  const handleCancel = useCallback(async (r: RecoverableExpenseDto) => {
    const ok = await confirmDelete({ resourceLabel: `expense ${r.expenseNumber || ''}`, confirmLabel: 'Cancel Expense' });
    if (!ok) return;
    try {
      await recoverableExpenseService.cancel(r.id, r.rowVersion);
      notificationService.toast.success('Expense cancelled.');
      load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Could not cancel.', 7000);
    }
  }, [confirmDelete, load]);

  const columns: SyutGridColumn<RecoverableExpenseDto>[] = useMemo(() => [
    { id: 'expenseNumber', header: 'Number', accessorKey: 'expenseNumber', size: 130,
      cell: ({ row }) => (row.original as RecoverableExpenseDto).expenseNumber || '—' },
    { id: 'expenseDate', header: 'Date', accessorKey: 'expenseDate', size: 110,
      cell: ({ row }) => ((row.original as RecoverableExpenseDto).expenseDate || '').slice(0, 10) },
    { id: 'partyName', header: 'Customer', accessorKey: 'partyName', size: 210 },
    { id: 'serviceName', header: 'Item', accessorKey: 'serviceName', size: 180 },
    {
      id: 'recovery', header: 'Recovered By', accessorKey: 'recoveryBusinessUnitName', size: 190,
      cell: ({ row }) => {
        const r = row.original as RecoverableExpenseDto;
        const bu = r.recoveryBusinessUnitName || '—';
        return r.isCrossEntity ? `${r.recoveryEntityCode || ''} — ${bu}` : bu;
      },
    },
    { id: 'amount', header: 'Amount', accessorKey: 'amount', size: 120,
      cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{fmtMoney((row.original as RecoverableExpenseDto).amount)}</span> },
    { id: 'pendingAmount', header: 'Pending', accessorKey: 'pendingAmount', size: 120,
      cell: ({ row }) => <span style={{ display: 'block', textAlign: 'right' }}>{fmtMoney((row.original as RecoverableExpenseDto).pendingAmount)}</span> },
    {
      id: 'reimbursementStatus', header: 'Status', accessorKey: 'reimbursementStatus', size: 150,
      cell: ({ row }) => {
        const r = row.original as RecoverableExpenseDto;
        if (r.docStatus === 'CANCELLED') return <span className="badge">Cancelled</span>;
        return <span className="badge">{STATUS_LABEL[r.reimbursementStatus || 'OPEN'] || 'Pending'}</span>;
      },
    },
  ], []);

  const rowActions = useCallback((r: RecoverableExpenseDto): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (can('DELETE') && r.docStatus === 'POSTED' && r.reimbursedAmount === 0) {
      items.push({ id: 'cancel', label: 'Cancel', icon: <IconSystem.trash size={18} />, onClick: () => handleCancel(r), variant: 'danger' });
    }
    return items;
  }, [can, handleCancel]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    { ...actionButtonFactory.add(handleAdd, 'Capture Expense'), visible: can('CREATE') },
    actionButtonFactory.refresh(load),
  ];

  const appliedCount = (queryParams.fromDate ? 1 : 0) + (queryParams.toDate ? 1 : 0)
    + (queryParams.reimbursementStatus ? 1 : 0) + (queryParams.includeCancelled ? 1 : 0);

  return (
    <EnterpriseScreenShell
      title="Recoverable Expenses"
      description="Expenses incurred on behalf of customers — recoverable, billed later on an invoice"
      icon={moduleIcon && (IconSystem as any)[moduleIcon] ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 }) : <IconSystem.file size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search expenses...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      customToolbarItems={
        <ColumnSelector columns={COLUMN_DEFS} visibleColumns={visibleColumns}
          onApply={applyColumns} onReset={resetColumns} />
      }
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <SyutSelect label="Reimbursement Status" options={STATUS_OPTIONS} value={statusFilter}
                  onChange={v => setStatusFilter((v as string) || '')} isClearable={false} size="sm"
                  menuPortalTarget={document.body} />
              </div>
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8125rem' }}>
                  <input type="checkbox" checked={includeCancelled}
                    onChange={e => setIncludeCancelled(e.target.checked)} /> Include cancelled
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => {
                setFromDate(''); setToDate(''); setStatusFilter(''); setIncludeCancelled(false);
                setQueryParams(p => ({ page: 1, pageSize: p.pageSize, searchTerm: p.searchTerm }));
              }}>Clear</button>
              <button className="btn btn-primary" onClick={() => {
                setQueryParams(p => ({
                  ...p,
                  fromDate: fromDate || undefined,
                  toDate: toDate || undefined,
                  reimbursementStatus: statusFilter || undefined,
                  includeCancelled: includeCancelled || undefined,
                  page: 1,
                }));
                setFilterOpen(false);
              }}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={columns} enableRowSelection
            visibleColumns={[...STRUCTURAL_COLUMN_IDS, ...visibleColumns.filter(id => !STRUCTURAL_COLUMN_IDS.includes(id))]}
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={(i) => setQueryParams(p => ({ ...p, page: i + 1 }))}
            onPageSizeChange={(s) => setQueryParams(p => ({ ...p, pageSize: s, page: 1 }))}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading...' : 'No recoverable expenses yet — click Capture Expense.'} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.map(r => (
                <div key={r.id} style={{
                  background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                  borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {r.expenseNumber || '—'} · {(r.expenseDate || '').slice(0, 10)}
                      </div>
                      <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: '0.2rem 0 0' }}>{r.partyName}</h3>
                    </div>
                    <ActionMenu items={rowActions(r)} />
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.serviceName}</div>
                  <div style={{ fontSize: '0.78rem' }}>
                    Recovered by <strong>{r.isCrossEntity ? `${r.recoveryEntityCode || ''} — ` : ''}{r.recoveryBusinessUnitName || '—'}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                    <span className="badge">
                      {r.docStatus === 'CANCELLED' ? 'Cancelled' : (STATUS_LABEL[r.reimbursementStatus || 'OPEN'] || 'Pending')}
                    </span>
                    <strong>{fmtMoney(r.amount)}</strong>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ExpenseFormModal isOpen={formOpen} onSaved={load} onClose={() => setFormOpen(false)}
        tabId={tabId} moduleName={moduleName || 'Recoverable Expenses'}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
        onRestoreModal={handleRestoreModal} />
    </EnterpriseScreenShell>
  );
};

export default RecoverableExpenseScreen;
